/**
 * Turning a pasted share link into something that can be played in the page.
 *
 * A teacher pastes whatever the recording tool gave them — a YouTube watch URL,
 * a `youtu.be` short link, a Drive "view" link. Only some of those can be put
 * in an `<iframe>`, and the rest have to stay honest links rather than an empty
 * black rectangle.
 *
 * One place, because both the library card and the lesson page ask the same
 * question and a second copy would answer it differently the first time a
 * provider changes (CLAUDE.md §3).
 */

export interface VideoEmbed {
  /** What to put in an `<iframe>`, or null when this link cannot be embedded. */
  embedUrl: string | null;
  /** Who is hosting it — for the label on the "open in a new tab" link. */
  provider: string;
}

/**
 * Providers that publish an embed path.
 *
 * Deliberately a short list rather than a guess: a link we cannot recognise is
 * offered as a link, which always works, instead of being framed and silently
 * refused by the host's `X-Frame-Options`.
 */
export function toVideoEmbed(videoUrl: string): VideoEmbed {
  let url: URL;
  try {
    url = new URL(videoUrl);
  } catch {
    return { embedUrl: null, provider: 'Video' };
  }

  const host = url.hostname.replace(/^www\./, '');

  // youtube.com/watch?v=ID  and  youtube.com/live/ID
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    const id = url.searchParams.get('v') ?? url.pathname.split('/').filter(Boolean).at(-1);
    return {
      embedUrl: id ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}` : null,
      provider: 'YouTube',
    };
  }

  // youtu.be/ID
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1);
    return {
      embedUrl: id ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}` : null,
      provider: 'YouTube',
    };
  }

  // vimeo.com/ID
  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean).at(-1);
    return {
      embedUrl: id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null,
      provider: 'Vimeo',
    };
  }

  // drive.google.com/file/d/ID/view
  if (host === 'drive.google.com') {
    const parts = url.pathname.split('/').filter(Boolean);
    const id = parts[0] === 'file' && parts[1] === 'd' ? parts[2] : undefined;
    return {
      embedUrl: id ? `https://drive.google.com/file/d/${encodeURIComponent(id)}/preview` : null,
      provider: 'Google Drive',
    };
  }

  // Everything else — an institute's own media server, a district portal, a Zoom
  // recording. Offered as a link, which is the one thing that always works.
  return { embedUrl: null, provider: host };
}

/**
 * A still to show for a linked lesson, or null when the provider publishes none.
 *
 * Only YouTube, and deliberately: its thumbnail lives at a fixed address that
 * needs no API call and no key. Vimeo and Drive both require a request signed
 * with credentials we would have to ship to the browser, so they get the
 * designed placeholder instead — which is a better card than a broken image.
 *
 * `hqdefault` is the one size guaranteed to exist for every video ever
 * uploaded; `maxresdefault` is not, and a card that sometimes 404s is worse
 * than one that is always a little soft. It arrives 4:3, so the card crops it
 * to 16:9 — which removes the letterbox bars rather than adding to them.
 */
export function toVideoThumbnail(videoUrl: string): string | null {
  const { embedUrl, provider } = toVideoEmbed(videoUrl);
  if (provider !== 'YouTube' || !embedUrl) return null;

  const id = embedUrl.split('/').at(-1);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}

/** Moved to `lib/format` for the platform dashboard; re-exported for the screens already using it here. */
export { formatDuration } from '@/lib/format';
